






















































































import { Component, Vue } from 'vue-property-decorator'
import AccountIdInput from '@/components/ui/AccountIdInput.vue'
import { DataTableHeader } from 'vuetify'
import { DefaultServerResponse } from '@/components/ui/TechTaskCard.vue'
import request from '@/helpers/requests'
import { Ripple } from 'vuetify/lib/directives'


// Data Objects
interface UserPermissions {
    twm: 1|0;
    dispatch: 1|0;
    analytics: 1|0;
    settings: 1|0;
}

enum RoleName {
    OWNER = 'Owner',
    ADMIN = 'Admin',
    USER = 'User'
}

enum AccessType {
    LOCATION = 'Location',
    ACCOUNT = 'Account',
    OWNER = 'Owner'
}

interface User {
    pulsemAccountId: number;
    username: string;
    roleName: RoleName; // 'Campaign'|'No Access'  Want to add 'Owner'
    active: 1|0;
    permissions: UserPermissions;
    accessType: AccessType;
}

// Action-specific request payloads
interface LoadRequestPayload {
    pulsemAccountId: number;
}

interface AddRequestPayload {
    pulsemAccountId: number;
    username: string;
    roleName: RoleName;
    active: 1|0;
    permissions: UserPermissions;
    accessType: AccessType;
}

interface SaveRequestPayload {
    pulsemAccountId: number;
    username: string;
    roleName: RoleName;
    active: 1|0;
    permissions: UserPermissions;
    accessType: AccessType;
}

interface RemoveRequestPayload {
    pulsemAccountId: number;
    username: string;
    roleName: RoleName;
    active: 1|0;
    permissions: UserPermissions;
    accessType: AccessType;
}

// Action-specific response payloads
interface LoadResponsePayload extends DefaultServerResponse {
    data: Array<User>;
}

interface AddResponsePayload extends DefaultServerResponse {
    data: User;
}

interface SaveResponsePayload extends DefaultServerResponse {
    data: User;
}

interface RemoveResponsePayload extends DefaultServerResponse {
    data: boolean|null;
}

@Component({
    components: {
        AccountIdInput
    },
    directives: {
        Ripple
    }
})
export default class UserManagement extends Vue {
    locationValid = false
    pulsemAccountId: number|null = null
    users: Array<User> = [];
    roles: Array<string> = [RoleName.USER, RoleName.ADMIN];
    access: Array<string> = [AccessType.LOCATION, AccessType.ACCOUNT]

    // Table handling properties
    isLoading = true; // This one is for AccountIdInput & full table loading
    headers: Array<DataTableHeader> = [
        { text: 'Username', value: 'username', align: 'start' },
        { text: 'Access Type', value: 'accessType', sortable: true },
        { text: 'Role', value: 'roleName', sortable: false },
        { text: 'Active', value: 'active', filterable: false, sortable: false },
        { text: 'Permissions', value: 'permissions', filterable: false, sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
    ];

    search = '';
    dialog = false;
    editedIndex = -1;
    editedItem: User = {
        pulsemAccountId: -1,
        username: '',
        roleName: RoleName.USER,
        active: 1,
        permissions: {
            twm: 0,
            dispatch: 0,
            analytics: 0,
            settings: 0
        },
        accessType: AccessType.LOCATION
    }; // TODO: const defaultUser; editedItem: User = defaultUser

    defaultItem: User = {
        pulsemAccountId: -1,
        username: '',
        roleName: RoleName.USER,
        active: 1,
        permissions: {
            twm: 0,
            dispatch: 0,
            analytics: 0,
            settings: 0
        },
        accessType: AccessType.LOCATION
    };

    usernameError = '';

    // Server requests
    // Move each to their own ts file & move dependencies
    async loadUsers(): Promise<LoadResponsePayload> {
        return await request.post('/support/user-management-load', {
            pulsemAccountId: this.pulsemAccountId
        } as LoadRequestPayload) as LoadResponsePayload
    }

    async addUser(user: User): Promise<AddResponsePayload> {
        return await request.post('/support/user-management-add/submit', {
            pulsemAccountId: this.pulsemAccountId,
            username: user.username,
            roleName: user.roleName,
            active: user.active,
            permissions: user.permissions,
            accessType: user.accessType
        } as AddRequestPayload) as AddResponsePayload
    }

    async saveUser(user: User): Promise<SaveResponsePayload> {
        return await request.post('/support/user-management-save/submit', {
            pulsemAccountId: this.pulsemAccountId,
            username: user.username,
            roleName: user.roleName,
            active: user.active,
            permissions: user.permissions,
            accessType: user.accessType
        } as SaveRequestPayload) as SaveResponsePayload
    }

    async removeUser(user: User): Promise<RemoveResponsePayload> {
        return await request.post('/support/user-management-remove/submit', {
            pulsemAccountId: this.pulsemAccountId,
            username: user.username,
            roleName: user.roleName,
            active: user.active,
            permissions: user.permissions,
            accessType: user.accessType
        } as RemoveRequestPayload) as RemoveResponsePayload
    }

    // Table row & dialog handling
    get formTitle(): string {
        return this.editedIndex === -1 ? 'New User' : 'Edit User'
    }

    editItem(item: User) {
        this.editedIndex = this.users.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.dialog = true
    }

    async deleteItem(item: User) {
        const index = this.users.indexOf(item)
        if (confirm('Are you sure you want to delete this user?')) {
            this.isLoading = true
            await this.removeUser(item)
            this.users.splice(index, 1)
        }
        this.isLoading = false
    }

    close() {
        this.dialog = false
        this.$nextTick(() => {
            this.editedItem = Object.assign({}, this.defaultItem)
            this.editedIndex = -1
        })
    }

    async save() {
        if (!this.editedItem.username) {
            this.usernameError = 'Username is required'
            return
        } else {
            this.usernameError = ''
        }
        if (this.editedIndex > -1) {
            this.isLoading = true
            await this.saveUser(this.editedItem)
            Object.assign(this.users[this.editedIndex], this.editedItem)
        } else {
            this.isLoading = true
            await this.addUser(this.editedItem)
            this.users.unshift(this.editedItem)
        }
        this.close()
        this.isLoading = false
    }

    // AccountIdInput support
    async onLocationChange(locationValid: boolean) {
        this.locationValid = locationValid
        this.users = []
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
        if (this.locationValid) {
            this.isLoading = true
            const response = await this.loadUsers()
            this.users = response.data
        } else {
            this.users = []
        }
        this.isLoading = false
    }

    onSuccess(status: boolean) {
        if (status) {
            this.pulsemAccountId = null
            this.locationValid = false
            this.users = []
            this.editedItem = Object.assign({}, this.defaultItem)
            this.editedIndex = -1
        }
    }
}
